import React, { useEffect, useState } from 'react';
import { Platform, View } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { useT } from '@transifex/react';

import { AuthLayout } from '../components/AuthLayout';
import { BloomButton } from '../components/BloomButton';
import { InlineError } from '../components/InlineError';
import Loading from '../components/Loading';
import { Logo } from '../components/Logo';
import ThemedText from '../components/ThemedText';
import { ROUTES } from '../utils/constants';
import { screenTestId } from '../utils/testId';
import { sanitizeRedirectUrl } from '../utils/redirect';
import { handleImpersonationCallback } from '../services/authService';
import { storeNextUrl } from '../services/storageService';
import { useLoginRouter } from '../hooks/useLoginRouter';
import { useOnAuthenticated } from '../hooks/useOnAuthenticated';

const tid = screenTestId('ImpersonationCallback');

// Redeems the one-shot code from hermes, stores the JWT + banner context,
// forwards to VRN. No analytics — SDK suppression on the VRN side is the gate.
export default function ImpersonationCallbackScreen() {
  const router = useLoginRouter();
  const params = useLocalSearchParams();
  const t = useT();
  const onAuthenticated = useOnAuthenticated();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = typeof params.code === 'string' ? params.code : undefined;
    let cancelled = false;

    (async () => {
      if (!code) {
        router.replace(ROUTES.LOGIN);
        return;
      }

      try {
        const { token, destination } = await handleImpersonationCallback(code);
        if (cancelled) return;

        const safe = sanitizeRedirectUrl(destination, ROUTES.LOGIN);

        if (onAuthenticated) {
          // Impersonation always targets a member.
          await onAuthenticated({ token, nextApp: 'member_app' });
        } else if (Platform.OS === 'web') {
          await storeNextUrl(safe);
          window.location.href = safe;
        } else {
          throw new Error(
            'Impersonation callback screen mounted on native without an onAuthenticated callback'
          );
        }
      } catch {
        if (cancelled) return;
        setError(t('Impersonation link is invalid or expired. Please try again from hermes.'));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleBackToLogin = () => {
    router.replace(ROUTES.LOGIN);
  };

  return (
    <AuthLayout>
      <View className='w-full'>
        <Logo />
        <View className='items-center justify-center py-16' testID={tid('container')}>
          {error ? (
            <>
              <InlineError message={error} testID={tid('error')} />
              <View className='mt-6 w-full'>
                <BloomButton
                  text={t('Back to sign in')}
                  onPress={handleBackToLogin}
                  testID={tid('back-button')}
                />
              </View>
            </>
          ) : (
            <>
              <Loading className='flex-none' />
              <ThemedText type='bodyRegular' color='subtle' className='mt-4'>
                {t('Starting impersonation session...')}
              </ThemedText>
            </>
          )}
        </View>
      </View>
    </AuthLayout>
  );
}
